import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { mapErrors } from "../../../Utils";
import { ResourceError } from "../../../utils/ResourceError";
import {
  ArrowTopRightOnSquareIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
  ClockIcon,
  LinkSlashIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import {
  fetchCustomerSystem,
  fetchCustomerSystems,
  fetchSystemTypes,
} from "../../../../api/customer/system";
import {
  createSystemAsync,
  updateSystemAsync,
} from "../../../../slices/customer/system";
import ProductSlideover from "../../products/product-slideover";
import { PMDialog } from "../../../utils/PMDialog";
import { ReportPreviousWorkDialog } from "../../service/reports/report-previous-work";
import SystemForm from "./SystemForm";
import { CustomerSelector } from "../../../utils/moduleSelectors/customerSelector";
import { SystemHistory } from "./SystemHistory";
import { axiosWithToast } from "../../../../operationFeedback";
import { TWclassNames } from "../../../utils/Div";

const MODE_EXISTING = "existing";
const MODE_CREATE = "create";
/** Show a search box in the existing-facility picker above this many rows. */
const PICKER_SEARCH_THRESHOLD = 5;

const CHIP_BUTTON =
  "inline-flex min-h-[40px] items-center gap-1.5 rounded-full border px-3 text-sm font-medium transition disabled:opacity-50";
const CHIP_NEUTRAL =
  "border-slate-200 bg-white text-slate-700 hover:bg-slate-50";
const CHIP_DANGER =
  "border-red-200 bg-white text-red-700 hover:bg-red-50";
const SECONDARY_BUTTON =
  "inline-flex min-h-[44px] items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50";
const PRIMARY_BUTTON_SX = {
  minHeight: 44,
  borderRadius: "12px",
  textTransform: "none",
  fontWeight: 700,
  background: "linear-gradient(135deg,#1463C7,#2D8BE8)",
  boxShadow: "0 12px 24px -8px rgba(20,99,199,.45)",
  "&:hover": { background: "linear-gradient(135deg,#0B3A78,#1463C7)" },
};

const facilityTitle = (sys) =>
  sys?.product?.title || sys?.system_m_name || sys?.serial_number || "";

const facilityMeta = (sys) =>
  [sys?.system_type?.title || sys?.type?.title, sys?.serial_number, sys?.placement]
    .filter(Boolean)
    .join(" · ");

export default function CustomerSystemDialog(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [itemState, setItemState] = useState({
    isLoading: false,
    error: false,
    errors: [],
    system: {},
    systems: [],
    systemsLoaded: false,
    systemTypes: [],
  });

  const {
    open,
    selectedSystem,
    customer,
    onClose,
    selected,
    onAttachSystem,
    module,
    moduleParam,
    onRefresh,
    caseId,
    allowLinkActions: allowLinkActionsProp,
  } = props;

  // Report context: attach/detach + pick existing facility. Customer page: edit/create only.
  const allowLinkActions =
    allowLinkActionsProp ?? module === "report";

  const selectedSystemId = selectedSystem || selected?.id || null;
  const {
    system,
    systems,
    systemsLoaded,
    systemTypes,
    isLoading,
    error,
    errors,
  } = itemState;
  const [readOnly, setReadOnly] = useState(props.readOnly);
  const [productSlideroverOpen, setProductSlideoverOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  // null = not chosen yet; resolved once the customer's facilities have loaded
  const [mode, setMode] = useState(null);
  const [pickerQuery, setPickerQuery] = useState("");

  const existingSystems = Array.isArray(systems) ? systems : [];
  const hasExistingSystems = existingSystems.length > 0;
  const isCreateFlow = !selectedSystemId && !system?.id;
  const canPickExisting = allowLinkActions && isCreateFlow && hasExistingSystems;
  const effectiveMode = mode ?? (canPickExisting ? MODE_EXISTING : MODE_CREATE);
  const isPickingExisting = canPickExisting && effectiveMode === MODE_EXISTING;
  // Don't flash the form before we know whether there are facilities to pick from
  const isWaitingForSystems =
    allowLinkActions && isCreateFlow && !!customer?.id && !systemsLoaded;

  const getCustomerSystems = async (customerId) => {
    return fetchCustomerSystems(customerId)
      .then((res) => {
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          systemsLoaded: true,
          systems: res?.data?.data ?? [],
        }));
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err?.data?.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          systemsLoaded: true,
          systems: [],
          error: true,
          errors: errors,
        }));
      });
  };

  const getSystemTypes = async () => {
    return fetchSystemTypes()
      .then((res) => {
        if (res?.data) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            systemTypes: res?.data,
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err?.data?.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          error: true,
          errors: errors,
        }));
      });
  };

  const getCustomerSystem = async () => {
    setItemState((prevState) => ({ ...prevState, isLoading: true }));

    return fetchCustomerSystem(customer?.id, selectedSystemId)
      .then((res) => {
        if (res?.data?.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            system: res.data,
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err?.data?.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          system: {},
          error: true,
          errors: errors,
        }));
      });
  };

  useEffect(() => {
    if (open) {
      setReadOnly(props.readOnly);
      setMode(null);
      setPickerQuery("");
      setItemState((prevState) => ({
        ...prevState,
        error: false,
        errors: [],
        systemsLoaded: false,
      }));
      if (selected?.id) {
        setItemState((prevState) => ({
          ...prevState,
          system: selected,
        }));
      } else if (!selectedSystemId) {
        setItemState((prevState) => ({
          ...prevState,
          system: {},
        }));
      }
      if (selectedSystemId) {
        getCustomerSystem();
      }
      if (customer?.id) {
        getCustomerSystems(customer.id);
      }
      getSystemTypes();
    }
  }, [open, selectedSystemId, customer?.id]);

  // Empty create form (customer page or report attach) — leave edit mode on
  useEffect(() => {
    if (open && !selectedSystemId) {
      setReadOnly(false);
    }
  }, [open, selectedSystemId]);

  const filteredSystems = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return existingSystems;
    return existingSystems.filter((sys) =>
      `${facilityTitle(sys)} ${facilityMeta(sys)}`.toLowerCase().includes(q)
    );
  }, [existingSystems, pickerQuery]);

  const createOrUpdateDispatch = (data) => {
    const payload = {
      ...data,
      description: data.description || data.other_comment || "",
      other_comment: data.other_comment || data.description || "",
      case_id: data.case_id || caseId || null,
    };
    if (system?.id) {
      return dispatch(updateSystemAsync(payload));
    }
    return dispatch(createSystemAsync(payload));
  };

  const startCreateNew = () => {
    setReadOnly(false);
    setMode(MODE_CREATE);
    setItemState((prev) => ({ ...prev, system: {} }));
  };

  const handleDetachSystem = () => {
    if (onAttachSystem) {
      onAttachSystem({ value: null });
      onRefresh?.({ id: moduleParam, detached: true });
    }
  };

  const dialogTitle = readOnly
    ? t("customer.systems.dialog.view_title")
    : system?.id
      ? t("customer.systems.dialog.edit_title")
      : t("customer.systems.dialog.create_title");

  const customerName = system?.customer?.name || customer?.name || null;
  const showCustomerSelector = !!system?.id || !customer?.id;

  const header = (
    <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-100 pb-3 sm:pb-4">
      <div className="min-w-0">
        <h3 className="truncate text-lg font-semibold leading-6 text-slate-900">
          {dialogTitle}
        </h3>
        {system?.id ? (
          <p className="mt-0.5 truncate text-sm text-slate-500">
            {[facilityTitle(system), customerName].filter(Boolean).join(" · ")}
          </p>
        ) : customerName ? (
          <p className="mt-0.5 truncate text-sm text-slate-500">{customerName}</p>
        ) : null}
      </div>
      <button
        type="button"
        className="-mr-1 -mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900"
        onClick={onClose}
        aria-label={t("Close")}
      >
        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  );

  const modeSwitch = canPickExisting ? (
    <div
      role="tablist"
      aria-label={t("customer.systems.dialog.create_title")}
      className="grid shrink-0 grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1"
    >
      {[
        { key: MODE_EXISTING, label: t("customer.systems.mode.existing") },
        { key: MODE_CREATE, label: t("customer.systems.mode.create") },
      ].map((tab) => {
        const active = effectiveMode === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => (tab.key === MODE_CREATE ? startCreateNew() : setMode(tab.key))}
            className={TWclassNames(
              "min-h-[40px] rounded-lg px-3 text-sm font-semibold transition",
              active
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  ) : null;

  // Actions for an existing facility. Chips wrap on phones instead of overflowing the header.
  const toolbar = system?.id ? (
    <div className="flex shrink-0 flex-wrap gap-2">
      <button
        type="button"
        className={TWclassNames(CHIP_BUTTON, CHIP_NEUTRAL)}
        onClick={() => setHistoryDialogOpen(true)}
      >
        <ClockIcon className="h-4 w-4" aria-hidden="true" />
        {t("customer.systems.history")}
      </button>
      {system?.product?.id ? (
        <>
          <button
            type="button"
            className={TWclassNames(CHIP_BUTTON, CHIP_NEUTRAL)}
            onClick={() => setProductSlideoverOpen(true)}
          >
            <WrenchScrewdriverIcon className="h-4 w-4" aria-hidden="true" />
            {t("customer.systems.spare_parts")}
          </button>
          <Link
            className={TWclassNames(CHIP_BUTTON, CHIP_NEUTRAL)}
            to={"/dashboard/products/" + system.product.id}
            target="_blank"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
            {t("customer.systems.open_product")}
          </Link>
        </>
      ) : null}
      {allowLinkActions ? (
        <>
          <button
            type="button"
            className={TWclassNames(CHIP_BUTTON, CHIP_NEUTRAL)}
            onClick={startCreateNew}
          >
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            {t("customer.systems.create_new")}
          </button>
          <button
            type="button"
            className={TWclassNames(CHIP_BUTTON, CHIP_DANGER)}
            onClick={handleDetachSystem}
          >
            <LinkSlashIcon className="h-4 w-4" aria-hidden="true" />
            {t("customer.systems.detach")}
          </button>
        </>
      ) : null}
    </div>
  ) : null;

  const existingSystemsPicker = () => (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">
        {t("customer.systems.select_existing_hint")}
      </p>
      {existingSystems.length > PICKER_SEARCH_THRESHOLD ? (
        <label className="relative block">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={pickerQuery}
            onChange={(e) => setPickerQuery(e.target.value)}
            placeholder={t("customer.systems.search_existing")}
            className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1463C7] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
          />
        </label>
      ) : null}
      {filteredSystems.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-500">
          {t("customer.systems.no_existing_match")}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {filteredSystems.map((sys) => (
            <li key={sys.id}>
              {/* Whole row is the tap target — no tiny "Select" button on phones */}
              <button
                type="button"
                onClick={() => onAttachSystem?.({ value: sys.id })}
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-blue-50/60 active:bg-blue-50"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-900">
                    {facilityTitle(sys) || t("customer.systems.not_set")}
                  </div>
                  <div className="truncate text-xs text-slate-500">
                    {facilityMeta(sys) || t("customer.systems.no_meta")}
                  </div>
                </div>
                <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-slate-400">
        {t("customer.systems.existing_count", { count: existingSystems.length })}
      </p>
    </div>
  );

  const systemForm = (formik) => (
    <div className="space-y-6">
      {showCustomerSelector ? (
        <div className="space-y-1.5">
          <CustomerSelector
            name="customer_id"
            label={t("customer.systems.linked_customer")}
            selected={system?.customer || customer}
            disabled={readOnly}
            {...formik}
          />
          {system?.id && !readOnly ? (
            <p className="text-xs text-slate-500">{t("customer.systems.move_hint")}</p>
          ) : null}
        </div>
      ) : null}

      <SystemForm formik={formik} systemTypes={systemTypes} readOnly={readOnly} />
      {system?.id && <SystemHistory systemId={system.id} />}
    </div>
  );

  const footer = (formik) => (
    <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-slate-100 pt-3 pb-[env(safe-area-inset-bottom)] sm:flex-row sm:items-center sm:justify-end sm:pt-4">
      <button type="button" className={SECONDARY_BUTTON} onClick={onClose}>
        {t("Close")}
      </button>
      {isPickingExisting ? (
        <LoadingButton
          type="button"
          variant="contained"
          startIcon={<PlusIcon className="h-5 w-5" />}
          sx={PRIMARY_BUTTON_SX}
          onClick={startCreateNew}
        >
          {t("customer.systems.create_new")}
        </LoadingButton>
      ) : readOnly ? (
        <LoadingButton
          type="button"
          variant="contained"
          startIcon={<PencilIcon className="h-5 w-5" />}
          sx={PRIMARY_BUTTON_SX}
          onClick={() => setReadOnly(false)}
        >
          {t("Edit")}
        </LoadingButton>
      ) : (
        <LoadingButton
          loading={formik.isSubmitting}
          disabled={isLoading}
          loadingPosition="start"
          startIcon={<CheckBadgeIcon className="h-5 w-5" />}
          type="button"
          variant="contained"
          sx={PRIMARY_BUTTON_SX}
          onClick={formik.submitForm}
        >
          {system?.id ? t("Update") : t("Create")}
        </LoadingButton>
      )}
    </div>
  );

  const displaySystem = () => (
    <Formik
      enableReinitialize
      initialValues={{
        id: system?.id,
        customer_id: system?.id ? (system.customer_id ?? system.customer?.id ?? null) : (customer?.id ?? null),
        product: system?.product,
        product_id: system?.product_id,
        system_m_name: system?.system_m_name,
        serial_number: system?.serial_number,
        installation_year: system?.installation_year,
        production_year: system?.production_year,
        system_type: system?.system_type || system?.type,
        system_type_id: system?.type?.id || system?.system_type?.id || system?.system_type_id,
        placement: system?.placement || "",
        delivery_address_id:
          system?.delivery_address_id || system?.delivery_address?.id || null,
        delivery_address: system?.delivery_address || null,
        other_comment:
          system?.description || system?.other_comment || system?.notes || "",
        case_id: caseId || system?.case_id || null,
        submit: null,
      }}
      validationSchema={Yup.object({
        serial_number: Yup.string().nullable(),
      })}
      onSubmit={async (values, helpers) => {
        try {
          await axiosWithToast(createOrUpdateDispatch(values).unwrap().then((data) => {
            if (!data?.id) throw new Error(t("customer.systems.save_failed"));
            return { data };
          }), (data) => {
            onClose(true);
            onAttachSystem?.({ value: data.id });
            onRefresh?.(data);
          }, () => {}, () => {}, t("Saving...."));
        } catch (err) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err?.safeMessage || err?.message || t("customer.systems.save_failed") });
        } finally { helpers.setSubmitting(false); }
      }}
    >
      {(formik) => (
        <Form className="flex min-h-0 flex-1 flex-col">
          {modeSwitch || toolbar ? (
            <div className="shrink-0 space-y-3 pt-3 sm:pt-4">
              {modeSwitch}
              {toolbar}
            </div>
          ) : null}

          {/* Only this region scrolls; header and footer stay put on phones */}
          <div className="-mx-3 min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-3 py-4 sm:-mx-6 sm:px-6">
            {isWaitingForSystems ? (
              <ResourceLoading />
            ) : isPickingExisting ? (
              existingSystemsPicker()
            ) : (
              systemForm(formik)
            )}
            {formik.errors.submit && (
              <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">
                {formik.errors.submit}
              </p>
            )}
          </div>

          {footer(formik)}
        </Form>
      )}
    </Formik>
  );

  return (
    <>
      <PMDialog open={open} onClose={onClose} size="3xl">
        <div className="flex min-h-0 flex-1 flex-col">
          {header}
          {isLoading ? (
            <div className="py-10">
              <ResourceLoading />
            </div>
          ) : error ? (
            <div className="py-6">
              <ResourceError errors={errors} />
            </div>
          ) : (
            displaySystem()
          )}
        </div>
      </PMDialog>

      <ProductSlideover
        open={productSlideroverOpen}
        onClose={() => setProductSlideoverOpen(false)}
        view="spareparts"
        productId={system?.product?.id}
        module={module}
        moduleParam={moduleParam}
      />
      <ReportPreviousWorkDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        customerId={
          customer?.id ||
          system?.customer_id ||
          system?.customer?.id ||
          null
        }
        customerSystemId={system?.id}
        title="report.previous_work.facility_history_title"
        showFacilityBadge={false}
        emptyKey="report.previous_work.facility_history_empty"
      />
    </>
  );
}

CustomerSystemDialog.defaultProps = {
  selected: null,
  readOnly: false,
  open: false,
  onClose: () => toast.error("Missing onClose function. Cannot close dialog"),
};
